import { useEffect, useState } from "react";
import { useAuthStore } from "@/stores/auth-store";

/**
 * Fetches an image URL with the user's JWT Bearer token and returns a blob URL.
 * This is necessary because <img src="..."> cannot send Authorization headers.
 *
 * Returns { src, loading, error } where src is a blob:// URL when loaded.
 */
export function useAuthenticatedImage(proxyUrl: string | null): {
  src: string | null;
  loading: boolean;
  error: boolean;
} {
  const [src, setSrc] = useState<string | null>(null);
  const [loading, setLoading] = useState(!!proxyUrl);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!proxyUrl) {
      setSrc(null);
      setLoading(false);
      setError(false);
      return;
    }

    let revoked = false;
    let blobUrl: string | null = null;
    const controller = new AbortController();

    setLoading(true);
    setError(false);

    const token = useAuthStore.getState().accessToken;
    const headers: Record<string, string> = {};
    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }

    fetch(proxyUrl, { headers, signal: controller.signal })
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.blob();
      })
      .then((blob) => {
        if (revoked) return;
        blobUrl = URL.createObjectURL(blob);
        setSrc(blobUrl);
        setLoading(false);
      })
      .catch((err) => {
        if (revoked) return;
        if (err.name !== "AbortError") {
          setError(true);
          setLoading(false);
        }
      });

    return () => {
      revoked = true;
      controller.abort();
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    };
  }, [proxyUrl]);

  return { src, loading, error };
}

/**
 * Triggers an authenticated file download by fetching the URL with Bearer auth
 * and creating a temporary download link from the resulting blob.
 */
export function useAuthenticatedDownload(): (url: string, filename: string) => void {
  return (url: string, filename: string) => {
    const token = useAuthStore.getState().accessToken;
    const headers: Record<string, string> = {};
    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }

    fetch(url, { headers })
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.blob();
      })
      .then((blob) => {
        const blobUrl = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = blobUrl;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(blobUrl);
      })
      .catch((err) => {
        console.error("Download failed:", err);
      });
  };
}
